import React, { useEffect, useState } from "react";
import { Container, Typography, useTheme, useMediaQuery, Grid } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchData } from "./api/fetchData";
import LowestPrice from "./LowestPrice";
import Loader from "./Loader";
import SearchBar from "./SearchBar";
import FilteredData from "./FilteredData";
import PostalCodePrompt from "./PostalCodePrompt";
import FeedbackPopup from "./FeedbackPopup";
import isMobile from "./isMobile";

const FetchLowestPrice = () => {
  const [query, setQuery] = useState("");
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [lowestPrice, setLowestPrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState(
    JSON.parse(localStorage.getItem("products"))
  );
  const [merchants, setMerchants] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedMerchant, setSelectedMerchant] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [searchCount, setSearchCount] = useState(0); // Track search activity
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false); // Show feedback popup
  const mobileView = isMobile();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const searchQuery = queryParams.get("search");

    const metaDescription = searchQuery
      ? `Search and compare the lowest prices for ${searchQuery} at Savepenny. Find the best deals from top merchants like Walmart, Food Basics, and more.`
      : "Find the best deals and price comparisons for top retailers like Walmart, Food Basics, Real Canadian Superstore, and more with Savepenny.";

    const metaTitle = searchQuery
      ? `Find Best Deals for ${searchQuery} | Savepenny`
      : "Compare Product Prices | Savepenny - Walmart, Food Basics, and More";

    const metaKeywords = searchQuery
      ? `${searchQuery}, price comparison, Savepenny, Walmart flyer, Food Basics, Real Canadian Superstore flyer, Flipp, Reebee`
      : "Walmart flyer, Food Basics, Real Canadian Superstore flyer, Flipp, Reebee, price comparison, Savepenny, best deals, Canadian shopping, flyer comparison";

    // OG meta tags
    const ogTitle = metaTitle;
    const ogDescription = metaDescription;
    const ogImage = "/logo.png";

    // Dynamically update meta tags
    document.title = metaTitle;

    // Update meta description tag
    let metaDescriptionTag = document.querySelector('meta[name="description"]');
    if (!metaDescriptionTag) {
      metaDescriptionTag = document.createElement("meta");
      metaDescriptionTag.setAttribute("name", "description");
      document.head.appendChild(metaDescriptionTag);
    }
    metaDescriptionTag.setAttribute("content", metaDescription);

    // Update meta keywords tag
    let metaKeywordsTag = document.querySelector('meta[name="keywords"]');
    if (!metaKeywordsTag) {
      metaKeywordsTag = document.createElement("meta");
      metaKeywordsTag.setAttribute("name", "keywords");
      document.head.appendChild(metaKeywordsTag);
    }
    metaKeywordsTag.setAttribute("content", metaKeywords);

    // Update OG meta tags
    let ogTitleTag = document.querySelector('meta[property="og:title"]');
    if (!ogTitleTag) {
      ogTitleTag = document.createElement("meta");
      ogTitleTag.setAttribute("property", "og:title");
      document.head.appendChild(ogTitleTag);
    }
    ogTitleTag.setAttribute("content", ogTitle);

    let ogDescriptionTag = document.querySelector(
      'meta[property="og:description"]'
    );
    if (!ogDescriptionTag) {
      ogDescriptionTag = document.createElement("meta");
      ogDescriptionTag.setAttribute("property", "og:description");
      document.head.appendChild(ogDescriptionTag);
    }
    ogDescriptionTag.setAttribute("content", ogDescription);

    let ogImageTag = document.querySelector('meta[property="og:image"]');
    if (!ogImageTag) {
      ogImageTag = document.createElement("meta");
      ogImageTag.setAttribute("property", "og:image");
      document.head.appendChild(ogImageTag);
    }
    ogImageTag.setAttribute("content", ogImage);

    let ogUrlTag = document.querySelector('meta[property="og:url"]');
    if (!ogUrlTag) {
      ogUrlTag = document.createElement("meta");
      ogUrlTag.setAttribute("property", "og:url");
      document.head.appendChild(ogUrlTag);
    }
    ogUrlTag.setAttribute("content", window.location.href);

    if (searchQuery) {
      setQuery(searchQuery);
      setSearchCount((prev) => prev + 1); // Increment search count on new search
    }
  }, [location.search]);

  useEffect(() => {
    if (searchCount === 3 && !localStorage.getItem("feedbackGiven")) {
      setShowFeedbackPopup(true);
    }
  }, [searchCount]);

  useEffect(() => {
    if (selectedMerchant !== "") {
      setLoading(true);

      const filteredItems = data.filter(
        (item) =>
          item.merchant === selectedMerchant ||
          item.merchant_name === selectedMerchant
      );

      if (filteredItems.length === 0) {
        setSelectedMerchant("");
      }

      const sortedItems = filteredItems.sort(
        (a, b) =>
          a.current_price - b.current_price ||
          a.original_price - b.original_price
      );

      setFilteredData(sortedItems);
      setLowestPrice(lowestPrice);

      setLoading(false);
    } else {
      setLoading(true);

      const sortedItems = data.sort(
        (a, b) =>
          a.current_price - b.current_price ||
          a.original_price - b.original_price
      );
      setFilteredData(sortedItems);
      setLowestPrice(lowestPrice);
      setLoading(false);
    }
  }, [selectedMerchant, data]);

  // Handle search
  const handleSearch = (inputValue) => {
    const value = inputValue.trim();
    if (value !== "") {
      // Update the URL with the search query using `useNavigate`
      navigate(`/?search=${value}`);
      if (postalCode) {
        fetchData(
          value,
          setLoading,
          setMerchants,
          setData,
          setLowestPrice,
          postalCode
        );
      } else {
        alert("Please enter a postal code to continue.");
      }
    }
  };

  useEffect(() => {
    if (postalCode) {
      fetchData(
        query,
        setLoading,
        setMerchants,
        setData,
        setLowestPrice,
        postalCode
      );
    }
  }, [postalCode]);

  const handleFeedbackClose = () => {
    setShowFeedbackPopup(false);
    localStorage.setItem("feedbackGiven", "true"); // Prevent future prompts
  };

  if (!postalCode) {
    return <PostalCodePrompt setPostalCode={setPostalCode} />;
  }

  return (
    <Container
      style={{ marginTop: mobileView ? "10px" : "20px", padding: "0 16px" }}
    >
      <Typography
        variant={mobileView ? "h6" : "h4"}
        component="h1"
        gutterBottom
        style={{ textAlign: "center" }}
      >
        Compare Grocery Prices
      </Typography>

      <SearchBar
        query={query}
        setQuery={setQuery}
        selectedMerchant={selectedMerchant}
        setSelectedMerchant={setSelectedMerchant}
        merchants={merchants}
        handleSearch={handleSearch}
        filteredData={filteredData}
      />

      {loading && <Loader />}

      {!loading && filteredData.length > 0 ? (
        <FilteredData
          filteredData={filteredData}
          products={products}
          setProducts={setProducts}
        />
      ) : (
        <Grid item mt={3} xs={12}>
          <Typography
            variant={mobileView ? "p" : "h6"}
            style={{ fontWeight: "bold" }}
            color="text.secondary"
            align="center"
          >
            No Products Found, Please try something else!
          </Typography>
        </Grid>
      )}

      <FeedbackPopup open={showFeedbackPopup} onClose={handleFeedbackClose} />
    </Container>
  );
};

export default FetchLowestPrice;
